/**
 * Everything hero training says, and every number it decides with (CAM-02, GDS §13).
 *
 * Same discipline as `camera-readiness.ts`, for the same reason (D-010): what a five-year-old is
 * asked to do, how long they must hold it, and how a demonstrated movement becomes a detection
 * threshold are all things a playtest will want to change in one auditable file.
 *
 * Three groups:
 *
 *  - {@link CALIBRATION_STEP_LABELS} is **verbatim from GDS §13's seven numbered steps**;
 *    `tests/architecture/calibration-copy.test.ts` re-reads the design spec and fails if a word
 *    changes. They are the doc's words, not a child's ("Capture neutral pose" is exactly the kind
 *    of phrase GDS §12 warns against) — the child reads the prompts below, never these.
 *  - {@link INVENTED_CALIBRATION_COPY} is every child-facing word on the screen. GDS §13 contains
 *    no prompts, no encouragement and no buttons, so **all of it is invented and awaiting Jon's
 *    copy review** (the D-049(5)/D-061(4) pattern).
 *  - {@link CALIBRATION_TUNABLES} are the numbers. **All invented** (no document states a hold
 *    duration, a minimum movement or a threshold fraction), seeded to be gentle rather than
 *    accurate, and due for tuning with a real child at the physical session and Phase 6.
 */

import type { CameraMode } from '../../interaction/camera/readiness';

/** GDS §13's seven steps, verbatim. `reach` is the Compact substitute for `jump` (GDS §15). */
export const CALIBRATION_STEP_LABELS = {
  'stand-in-lane': 'Stand within lane guide',
  'neutral-pose': 'Capture neutral pose',
  'raise-hands': 'Raise both hands',
  crouch: 'Crouch',
  'move-in-lane': 'Move within lane',
  jump: 'Jump or perform substitute',
  reach: 'Jump or perform substitute',
  confirm: 'Confirm successful detection',
} as const;

/**
 * Copy no document contains. **Every string here is invented and awaiting approval.**
 *
 * Written as hero training (GDS §13), in the second person, never naming a mistake: a body the
 * camera cannot see is a fact about the room, and a movement that was too small is an invitation
 * to make a bigger one. Nothing here is competitive and nothing here is jargon.
 */
export const INVENTED_CALIBRATION_COPY = {
  /** Screen title. */
  pageTitle: 'Hero training',
  /** One line under the title. */
  pageLede: 'Show us your moves so the suit knows how you move.',
  /** The entry point on the readiness screen. */
  start: 'Start hero training',
  /** Leaves training early. */
  stop: 'Stop training',
  /** Starts the whole sequence again. */
  restart: 'Start again',
  /** Moves past a move the child cannot do today. */
  skip: 'Skip this move',
  /** The final step's button. */
  confirm: 'Yes, that is me',
  /** The done-for-now screen. */
  completeTitle: 'Training complete',
  completeBody: 'The suit knows your moves now.',
  /** Leaves the done-for-now screen. */
  done: 'Back',
  /** What the hero can do, listed on the done screen. */
  movesTitle: 'Moves the suit learned',
  /** Shown beside a move that was skipped. */
  moveSkipped: 'Skipped for now',
  moveLearned: 'Learned',
  /**
   * The mission handoff on the done-for-now screen (Slice D).
   *
   * Says what happens next in the child's own terms and promises nothing about being good at it.
   * The keyboard is never taken away by pressing it (CAM-09), so it does not need to warn anybody.
   */
  playWithCamera: 'Play with the camera',
  /** What each step asks the child to do. */
  prompts: {
    'stand-in-lane': 'Stand in the middle of the glowing ring.',
    'neutral-pose': 'Stand tall and still for a moment.',
    'raise-hands': 'Put both hands high in the air, then let them down.',
    crouch: 'Crouch down low, then stand back up.',
    'move-in-lane': 'Step all the way to one side, then all the way to the other.',
    jump: 'Jump up as high as you like, then land softly.',
    reach: 'Reach up as far as you can, then bring your arms down.',
    confirm: 'That is everything. Ready to go?',
  },
  /** Live feedback while a step is running. */
  feedback: {
    WAITING_FOR_BODY: 'Step into the picture so we can see you.',
    LOW_VISIBILITY: 'Move somewhere a bit brighter so we can see you.',
    OUT_OF_LANE: 'Come and stand inside the glowing ring.',
    HOLD_STILL: 'Lovely — hold it there.',
    SHOW_THE_MOVE: 'Show us the move whenever you are ready.',
    NEEDS_BIGGER_MOVE: 'Try that again, a little bigger.',
    MOVE_SEEN: 'Great — back to standing.',
    CAPTURED: 'Got it.',
    CONFIRM: 'That is everything.',
  },
} as const;

/**
 * The numbers hero training decides with. **All invented** (D-010/D-023 family).
 *
 * The one worth arguing about is {@link CalibrationTunables.thresholdFraction}: a detection
 * threshold is set at *half* of what the child demonstrated. A threshold at the full demonstrated
 * movement would only ever fire on a child's very best effort, which is not what a five-year-old
 * produces on the eleventh round of a mission.
 */
export const CALIBRATION_TUNABLES = {
  /** Continuous qualifying time before a steady step captures. */
  steadyHoldMs: 800,
  /** Continuous qualifying time before the neutral measurements are taken. */
  neutralHoldMs: 1_000,
  /** Minimum qualifying samples any capture needs, however fast frames arrive. */
  minSamples: 3,
  /** Fraction of the demonstrated movement that becomes the detection threshold. */
  thresholdFraction: 0.5,
  /** Half-width of the central lane guide, in mirrored x: where "stand in the ring" means. */
  laneGuideHalfWidth: 0.2,
  /** Smallest movement, in body scales, that counts as a demonstration rather than a fidget. */
  minimumMoveScale: {
    hands: 0.15,
    crouch: 0.2,
    jump: 0.15,
    reach: 0.2,
  },
  /** How far a movement must fall back from its peak before the step counts it as finished. */
  returnFraction: 0.4,
  /**
   * Fraction of the minimum movement that counts as *an attempt*.
   *
   * The difference between "show us the move" and "a little bigger": a child who has not moved
   * yet needs the first sentence and a child who tried needs the second, and telling a child who
   * just tried to try is how a five-year-old decides the game is not listening.
   */
  noticeFraction: 0.3,
  /** Smallest lateral travel, in mirrored x, that can define three lanes. */
  minLaneTravel: 0.12,
  /** Bounds every derived `…Scale` threshold is clamped into, so one bad capture cannot ruin a session. */
  scaleBounds: { min: 0.1, max: 0.6 },
  /** Lane hysteresis as a fraction of the derived lane width. */
  laneHysteresisFraction: 0.2,
  /** Architecture §20's reuse rules, as numbers. */
  reuse: {
    /** How much the viewport may change and still count as "similar". */
    viewportTolerance: 0.1,
    /** Quality below which a profile is not worth reusing. */
    minQuality: 0.5,
    /** Live quality below this fraction of the calibrated quality counts as confidence falling. */
    qualityFallFraction: 0.7,
  },
} as const;

export type CalibrationTunables = typeof CALIBRATION_TUNABLES;

/**
 * The play-space mode calibration is measured in.
 *
 * Full-Motion is the default (GDS §6's fuller mode, and the one the mission is written for);
 * Compact swaps the jump for the GDS §15 substitute. **The selection UI is Slice F** (D-059:
 * parent choice with a readiness-derived recommended default) — until it ships, this is the value
 * hero training is handed.
 */
export const DEFAULT_CALIBRATION_CAMERA_MODE: CameraMode = 'full-motion';
